import Link from "next/link";
import { Newspaper } from "lucide-react";
import { apiFetch } from "@/lib/api";
import type { SiteTemplateKey } from "@/lib/siteTemplates";

interface NewsItem {
  title: string;
  url: string;
  excerpt?: string;
  imageUrl?: string;
  publishedAt?: string;
  source?: string;
}

export async function NewsFeedBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "Latest News");
  const subtitle = String(content.subtitle ?? "");
  const limit = typeof content.limit === "number" ? content.limit : 3;
  const category = content.category ? String(content.category) : undefined;

  const items = await apiFetch<NewsItem[]>(`/v1/pages/news-feed?limit=${limit}${category ? `&category=${encodeURIComponent(category)}` : ""}`).catch(
    () => []
  );

  return (
    <section data-template={template} className="bg-[var(--color-surface-muted)] py-[calc(var(--space-unit)*4)]">
      <div className="mx-auto max-w-7xl px-4">
        <h2
          className="font-bold text-[var(--color-text)]"
          style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)", lineHeight: "var(--line-heading)" }}
        >
          {heading}
        </h2>
        {subtitle && <p className="mt-2 text-[var(--color-text)]/70">{subtitle}</p>}

        {items.length === 0 ? (
          <p className="mt-6 text-[var(--color-text)]/60">No news items are available right now.</p>
        ) : (
          <ul className="mt-6 grid gap-5 md:grid-cols-3">
            {items.map((item, index) => (
              <li key={index}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-[rgb(var(--color-primary))]">
                    <Newspaper className="h-4 w-4" aria-hidden />
                    {item.source || "News"}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-[var(--color-text)] group-hover:text-[rgb(var(--color-primary))]">
                    {item.title}
                  </h3>
                  {item.excerpt && <p className="mt-2 line-clamp-3 text-sm text-[var(--color-text)]/70">{item.excerpt}</p>}
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6">
          <Link
            href="/blog"
            className="inline-flex min-h-[44px] items-center justify-center rounded-[var(--border-radius)] bg-[rgb(var(--color-primary))] px-5 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            More updates
          </Link>
        </div>
      </div>
    </section>
  );
}
